import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { HelpCircle, ChevronDown, ChevronUp, Search, Truck, RefreshCw, CreditCard, Package, MessageCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import Footer from '../components/Footer';

const FAQPage = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const faqCategories = [
    {
      title: 'Ordering & Payment',
      icon: CreditCard,
      faqs: [
        {
          question: 'What payment methods do you accept?',
          answer: 'We accept all major credit cards (Visa, Mastercard, American Express), PayPal, Apple Pay, Google Pay, and Afterpay/Zip for buy-now-pay-later options.'
        },
        {
          question: 'Can I modify or cancel my order?',
          answer: 'Please contact us within 2 hours of placing your order if you need to make changes. Once an order has been processed or shipped, we cannot modify or cancel it.'
        },
        {
          question: 'Do you offer gift wrapping?',
          answer: 'Yes! All our items come beautifully packaged in gift-ready boxes at no extra charge. If you\'d like a personalised gift message included, just add a note at checkout.'
        },
        {
          question: 'Are prices in Australian dollars?',
          answer: 'Yes, all prices on our website are in Australian Dollars (AUD). International customers will see converted prices at checkout based on current exchange rates.'
        }
      ]
    },
    {
      title: 'Shipping & Delivery',
      icon: Truck,
      faqs: [
        {
          question: 'Do you offer free shipping?',
          answer: 'Yes! We offer FREE standard shipping on all orders within Australia. No minimum purchase required.'
        },
        {
          question: 'How long does shipping take?',
          answer: 'Standard delivery within Australia takes 5-10 business days. Metro areas typically receive orders within 5-7 days, while regional areas may take 7-10 days.'
        },
        {
          question: 'Do you ship internationally?',
          answer: 'Yes, we can ship internationally on request. Please contact us for a shipping quote to your country. International delivery typically takes 10-21 business days.'
        },
        {
          question: 'How can I track my order?',
          answer: 'Once your order ships, you\'ll receive an email with your tracking number. You can track your package on our Track Order page or directly through Australia Post.'
        }
      ]
    },
    {
      title: 'Returns & Refunds',
      icon: RefreshCw,
      faqs: [
        {
          question: 'What is your return policy?',
          answer: 'We accept returns within 30 days of delivery for both defective and non-defective products. Items must be unused and in their original packaging.'
        },
        {
          question: 'How do I return an item?',
          answer: 'Contact us via email or WhatsApp with your order number and reason for return. We\'ll provide you with return instructions within 24-48 hours.'
        },
        {
          question: 'Can I return earrings?',
          answer: 'For hygiene reasons, earrings cannot be returned unless they are defective. If your earrings arrived damaged, please contact us immediately for a replacement.'
        },
        {
          question: 'How long do refunds take?',
          answer: 'Once we receive and inspect your return, refunds are processed within 5-7 business days back to your original payment method.'
        }
      ]
    },
    {
      title: 'Products & Care',
      icon: Package,
      faqs: [
        {
          question: 'Are your products handmade?',
          answer: 'Yes! Every single piece is handcrafted by us in our Australian studio. This means each item is unique and may have slight variations that make it special.'
        },
        {
          question: 'What materials do you use?',
          answer: 'We use high-quality, non-toxic epoxy resin along with various embellishments like dried flowers, glitter, and pigments. All our materials are safe and durable.'
        },
        {
          question: 'How do I care for my resin jewellery?',
          answer: 'Keep your resin pieces away from direct sunlight and extreme heat. Clean gently with a soft, damp cloth. Avoid contact with perfumes, lotions, and harsh chemicals.'
        },
        {
          question: 'Can I request a custom order?',
          answer: 'Absolutely! We love creating custom pieces. Contact us via WhatsApp or email with your idea and we\'ll work together to bring your vision to life.'
        }
      ]
    }
  ];

  // Filter FAQs based on search
  const filteredCategories = faqCategories.map(category => ({
    ...category,
    faqs: category.faqs.filter(faq => 
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(category => category.faqs.length > 0);

  // FAQ Schema for SEO
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqCategories.flatMap(cat => cat.faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    })))
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-white to-teal-50">
      <Helmet>
        <title>FAQ | Frequently Asked Questions | Charming Creations</title>
        <meta name="description" content="Find answers to common questions about Charming Creations orders, shipping, returns, and products. Free shipping Australia-wide, 30-day returns, handcrafted resin art." />
        <link rel="canonical" href="https://www.charmingcreationsgifts.com/faq" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-amber-200 shadow-sm sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_be17761e-04ff-4edf-8e1f-841eccc40e55/artifacts/she6yj1e_IMG_6151.png" 
                alt="Charming Creations Logo" 
                className="w-12 h-12 object-contain"
              />
              <div className="hidden sm:block">
                <h1 className="text-xl font-bold bg-gradient-to-r from-amber-600 to-teal-600 bg-clip-text text-transparent">
                  Charming Creations
                </h1>
              </div>
            </Link>
            <Link to="/">
              <Button variant="outline" className="border-amber-300 hover:bg-amber-50">
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 bg-gradient-to-r from-purple-500 to-indigo-500 text-white">
        <div className="container mx-auto px-4 text-center">
          <HelpCircle className="w-16 h-16 mx-auto mb-6 opacity-90" />
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Frequently Asked Questions</h1>
          <p className="text-xl opacity-90 max-w-2xl mx-auto mb-8">
            Find answers to common questions about orders, shipping, returns, and more
          </p>
          
          {/* Search */}
          <div className="max-w-md mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input 
              type="text"
              placeholder="Search FAQs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 py-6 text-gray-900 border-0 shadow-lg"
            />
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-4xl">
          {filteredCategories.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600 mb-4">No FAQs found matching "{searchQuery}"</p>
              <Button onClick={() => setSearchQuery('')} variant="outline">
                Clear Search
              </Button>
            </div>
          ) : (
            <div className="space-y-8">
              {filteredCategories.map((category, catIdx) => (
                <div key={catIdx}>
                  <div className="flex items-center gap-3 mb-4">
                    <category.icon className="w-6 h-6 text-amber-500" />
                    <h2 className="text-2xl font-bold text-gray-900">{category.title}</h2>
                  </div>
                  <div className="space-y-3">
                    {category.faqs.map((faq, faqIdx) => {
                      const globalIdx = `${catIdx}-${faqIdx}`;
                      const isOpen = openIndex === globalIdx;
                      
                      return (
                        <Card key={faqIdx} className="border-2 border-gray-100 hover:border-amber-200 transition-colors">
                          <button
                            onClick={() => setOpenIndex(isOpen ? null : globalIdx)}
                            className="w-full text-left"
                          >
                            <CardContent className="p-4 md:p-6">
                              <div className="flex items-center justify-between gap-4">
                                <h3 className="font-semibold text-gray-900 pr-4">{faq.question}</h3>
                                {isOpen ? (
                                  <ChevronUp className="w-5 h-5 text-amber-500 flex-shrink-0" />
                                ) : (
                                  <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                )}
                              </div>
                              {isOpen && (
                                <p className="mt-4 text-gray-600 leading-relaxed border-t border-gray-100 pt-4">
                                  {faq.answer}
                                </p>
                              )}
                            </CardContent>
                          </button>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Still Need Help */}
          <div className="mt-16 text-center bg-gradient-to-r from-amber-100 to-teal-100 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Still Have Questions?</h2>
            <p className="text-gray-600 mb-6">
              Can't find what you're looking for? We're here to help!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://wa.me/61415145952?text=Hi%2C%20I%20have%20a%20question"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button className="bg-green-500 hover:bg-green-600 text-white px-6">
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Chat on WhatsApp
                </Button>
              </a>
              <Link to="/contact">
                <Button variant="outline" className="border-2 border-amber-500 text-amber-600 hover:bg-amber-50 px-6">
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FAQPage;
